import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Stethoscope, Building2, ArrowRight, Clock, MapPin, FileCheck2, IdCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { track } from "@/lib/track";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth(intendedRole) {
  // Preserve the entry-point role so onboarding pre-selects it after Google auth.
  try {
    if (intendedRole) localStorage.setItem("lc_intended_role", intendedRole);
    else localStorage.removeItem("lc_intended_role");
  } catch (err) {
    // Private-browsing mode etc — non-fatal; the user just re-picks role in onboarding.
    console.warn("localStorage unavailable:", err);
  }
  track(intendedRole === "facility" ? "cta_signin_facility" : "cta_signin_professional", { intended_role: intendedRole });
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Landing() {
  const { user, loading } = useAuth();
  useEffect(() => { track("landing_view"); }, []);
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-4 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center">
              <Stethoscope className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">LocumConnect</span>
            <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-[0.2em] hidden sm:inline-flex">Pune · Mumbai · Bengaluru · more</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="landing-signin-professional"
              onClick={() => startGoogleAuth("professional")}
              variant="ghost"
              className="rounded-full text-slate-700 hover:bg-slate-100"
            >
              <Stethoscope className="w-4 h-4 mr-1.5" /> Find shifts
            </Button>
            <Button
              data-testid="landing-signin-facility"
              onClick={() => startGoogleAuth("facility")}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Building2 className="w-4 h-4 mr-1.5" /> Post a shift
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs uppercase tracking-[0.2em] text-emerald-800 mb-6">
              <ShieldCheck className="w-3 h-3" /> Verified locum marketplace
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-slate-900">
              Fill a shift.<br />Trust the badge.
            </h1>
            <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-lg">
              LocumConnect matches hospitals and clinics with pre‑verified locum doctors
              and nurses across various Indian cities. Council registration, degree, and
              government ID checked before anyone applies to your shift.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="landing-hero-professional-btn"
                onClick={() => startGoogleAuth("professional")}
                size="lg"
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-12 px-6"
              >
                <Stethoscope className="w-4 h-4 mr-1.5" /> Find shifts
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                data-testid="landing-hero-facility-btn"
                onClick={() => startGoogleAuth("facility")}
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-6 border-slate-300 text-slate-900 hover:bg-slate-50"
              >
                <Building2 className="w-4 h-4 mr-1.5" /> Post a shift
              </Button>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
              <MapPin className="w-3 h-3" /> Pune, Mumbai, Bengaluru, Delhi, Hyderabad, Chennai, Ahmedabad and more
            </div>
          </div>

          <div className="relative pb-14">
            <img
              src="https://images.pexels.com/photos/7789603/pexels-photo-7789603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Modern clinical space"
              className="rounded-3xl border border-slate-200 shadow-sm w-full object-cover aspect-[4/3]"
            />
            <div data-testid="hero-trust-card" className="absolute -bottom-2 left-4 right-4 md:left-6 md:right-auto md:max-w-[380px] bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-medium">Every professional verified</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-700">
                <span className="inline-flex items-center gap-1"><FileCheck2 className="w-3 h-3 text-emerald-700" /> Council reg</span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1"><FileCheck2 className="w-3 h-3 text-emerald-700" /> Degree</span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1"><IdCard className="w-3 h-3 text-emerald-700" /> Govt ID</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-role split */}
      <section className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-2 gap-6">
        <RoleCard
          testid="role-card-professional"
          icon={<Stethoscope className="w-5 h-5" />}
          eyebrow="For Doctors & Nurses"
          title="Get shifts. Get paid on time."
          bullets={[
            "Upload council registration, degree, and ID once",
            "Wear a verified badge every facility trusts",
            "See only shifts that match your specialty",
          ]}
          cta="Find shifts"
          onSignIn={() => startGoogleAuth("professional")}
        />
        <RoleCard
          testid="role-card-facility"
          icon={<Building2 className="w-5 h-5" />}
          eyebrow="For Hospitals & Clinics"
          title="Post a shift. Pick the right hire."
          bullets={[
            "Post shifts in under 60 seconds",
            "Only verified professionals can apply",
            "One tap to confirm your applicant",
          ]}
          cta="Post a shift"
          onSignIn={() => startGoogleAuth("facility")}
        />
      </section>

      {/* How it works */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-lg">
            Trust, then transaction.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <Step n="01" title="Verify" desc="Upload credentials. Our admin team reviews within 24 hours." icon={<ShieldCheck className="w-4 h-4" />} />
            <Step n="02" title="Match" desc="Facilities post shifts. Verified professionals see and apply." icon={<Clock className="w-4 h-4" />} />
            <Step n="03" title="Deliver" desc="Confirm, complete, and settle payment — all in one place." icon={<ArrowRight className="w-4 h-4" />} />
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} LocumConnect · Multi-city · <Link to="/privacy" className="hover:text-emerald-800 hover:underline underline-offset-2" data-testid="footer-privacy-link">Privacy</Link></span>
          <span className="uppercase tracking-[0.2em]">Matching service · Facilities own clinical supervision</span>
        </div>
      </footer>
    </div>
  );
}

function RoleCard({ testid, icon, eyebrow, title, bullets, cta, onSignIn }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-8 transition-shadow hover:shadow-md flex flex-col">
      <div className="flex items-center gap-2 text-emerald-800">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">{icon}</div>
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{eyebrow}</span>
      </div>
      <h3 className="font-display text-2xl font-bold mt-4 tracking-tight">{title}</h3>
      <ul className="mt-5 space-y-2 text-slate-600 flex-1">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2 text-sm">
            <span className="mt-2 w-1 h-1 rounded-full bg-emerald-700 shrink-0" />
            {b}
          </li>
        ))}
      </ul>
      {cta && onSignIn && (
        <Button
          onClick={onSignIn}
          className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11 self-start"
          data-testid={`${testid}-cta`}
        >
          {cta}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

function Step({ n, title, desc, icon }) {
  return (
    <div className="bg-[#FAFAFA] border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{n}</span>
        <span className="text-emerald-800">{icon}</span>
      </div>
      <div className="mt-4 font-display text-lg font-bold">{title}</div>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}
